import { Slot } from "@radix-ui/react-slot";
import type { ButtonHTMLAttributes, Ref } from "react";
import { cn } from "../lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "dark";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
  danger: "btn-danger",
  dark: "btn-dark",
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: "btn-sm",
  md: "",
  lg: "btn-lg",
};

interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows the inline spinner (the only place a spinner is allowed) and disables the button. */
  loading?: boolean;
  /** Render the single child element with button styling (e.g. a router link). */
  asChild?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/** Icon-only buttons must carry an accessible name. */
export type ButtonProps =
  | (BaseButtonProps & { iconOnly?: false })
  | (BaseButtonProps & { iconOnly: true; "aria-label": string });

export function buttonClasses(options: { variant?: ButtonVariant; size?: ButtonSize; iconOnly?: boolean } = {}) {
  const { variant = "primary", size = "md", iconOnly = false } = options;
  return cn("btn", VARIANT_CLASS[variant], SIZE_CLASS[size], iconOnly && "btn-icon");
}

function Spinner() {
  return (
    <svg
      data-testid="button-spinner"
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className="size-4 animate-spin motion-reduce:animate-none"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  iconOnly = false,
  loading = false,
  asChild = false,
  className,
  disabled,
  children,
  type,
  ...rest
}: ButtonProps) {
  const classes = cn(buttonClasses({ variant, size, iconOnly }), className);
  if (asChild) {
    return (
      <Slot className={classes} {...rest}>
        {children}
      </Slot>
    );
  }
  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}
